import JSONbig from 'json-bigint'
import { API_BASE } from './config.js'

const jsonParser = JSONbig({ storeAsString: true })

function getToken() {
  return localStorage.getItem('token') || ''
}

function request(method, path, body) {
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + getToken()
  }
  const options = {
    method,
    headers,
    credentials: 'include'
  }
  if (body !== undefined) {
    options.body = JSON.stringify(body)
  }
  return fetch(`${API_BASE}${path}`, options)
    .then(res => res.text())
    .then(text => jsonParser.parse(text))
}

/**
 * 查询评论列表
 * @param {string} resourceType WORKSPACE / PROJECT / TIMEBLOCK
 * @param {string|number} resourceId 资源 ID
 * @returns {Promise<{code:number,message:string,data:Array,success:boolean,timestamp:string}>}
 */
export function listComments(resourceType, resourceId) {
  const query = new URLSearchParams({
    resourceType,
    resourceId: String(resourceId)
  }).toString()
  return request('GET', `/api/v1/comments?${query}`)
}

/**
 * 发表评论（内容上限 100 字）
 * @param {Object} params
 * @param {string} params.resourceType WORKSPACE / PROJECT / TIMEBLOCK
 * @param {string|number} params.resourceId 资源 ID
 * @param {string} params.content 评论内容，1-100 字
 * @param {string|number} [params.parentId] 被回复的那条评论 ID（发顶层评论不传）。
 *   不要自己归一化到顶层：后端会把 parent_id 收敛到顶层评论，并用这条评论的作者当被回复者。
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function createComment(params) {
  return request('POST', '/api/v1/comments', params)
}

/**
 * 删除评论
 * @param {string|number} id 评论 ID
 * @returns {Promise<{code:number,message:string,data:null,success:boolean,timestamp:string}>}
 */
export function deleteComment(id) {
  return request('DELETE', `/api/v1/comments/${id}`)
}

/**
 * 点赞
 * @param {Object} params
 * @param {string} params.resourceType WORKSPACE / PROJECT / TIMEBLOCK
 * @param {string|number} params.resourceId 资源 ID
 * @returns {Promise<{code:number,message:string,data:null,success:boolean,timestamp:string}>}
 */
export function like(params) {
  return request('POST', '/api/v1/likes', params)
}

/**
 * 查询点赞状态
 * @param {string} resourceType WORKSPACE / PROJECT / TIMEBLOCK
 * @param {string|number} resourceId 资源 ID
 * @returns {Promise<{code:number,message:string,data:{liked:boolean,count:number},success:boolean,timestamp:string}>}
 */
export function getLikeStatus(resourceType, resourceId) {
  const query = new URLSearchParams({
    resourceType,
    resourceId: String(resourceId)
  }).toString()
  return request('GET', `/api/v1/likes/status?${query}`)
}

/**
 * 把后端返回的统计**列表**归一化成以资源 ID 为键的对象。
 *
 * 后端契约是 `[{ resourceId, commentCount, likeCount }]`（List 能在 OpenAPI 里描述清楚），
 * 而下游组件（ListPage / FriendTablePanel）习惯用 `interactions[blockId]` 直接索引 ——
 * 在这里转一次，调用方就不用各写一遍。
 *
 * ⚠️ 只转换成功响应；失败响应原样返回，让调用方照旧走 `res.success` 判断。
 */
function normalizeSummary(res) {
  if (!res || !res.success || res.code !== 200) return res
  const map = {}
  ;(res.data || []).forEach(item => {
    if (!item || item.resourceId == null) return
    map[String(item.resourceId)] = {
      commentCount: item.commentCount,
      likeCount: item.likeCount
    }
  })
  return { ...res, data: map }
}

/**
 * 批量查询互动统计（评论数 / 点赞数）
 *
 * <p>只统计**当前用户名下**的资源：好友在这些资源上留下的评论与点赞，其 owner 正是当前用户，
 * 因此会被正常计入；反过来不可能读到他人资源的互动情况。</p>
 *
 * 用途：表格页在时间块右下角展示互动角标。**一次请求覆盖整张表**，
 * 不要按时间块逐个请求（一周可能有几十个块）。
 *
 * @param {Object} params
 * @param {string} params.resourceType WORKSPACE / PROJECT / TIMEBLOCK
 * @param {Array<string|number>} params.resourceIds 资源 ID 列表
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 *   data 形如 `{ '<blockId>': { commentCount: 2, likeCount: 1 } }`；没有任何互动的资源不会出现在里面。
 *   注意后端实际返回的是**数组**，这里已归一化成对象，见 normalizeSummary
 */
export function getInteractionSummary(params) {
  const query = new URLSearchParams({
    resourceType: params.resourceType,
    resourceIds: (params.resourceIds || []).map(String).join(',')
  }).toString()
  return request('GET', `/api/v1/interactions/summary?${query}`).then(normalizeSummary)
}

/**
 * 批量查询「当前用户可见」资源的互动统计（评论数 / 点赞数）。
 *
 * <p>与 {@link getInteractionSummary} 的区别：后者只统计 owner_id == 当前用户的资源（自己表上好友的互动），
 * 本方法统计当前用户能看到的任意资源（含好友的时间块，其 owner 是好友），用于好友时间表页
 * 在时间块右下角展示互动角标，与「我的表格」页保持一致。</p>
 *
 * @param {Object} params
 * @param {string} params.resourceType WORKSPACE / PROJECT / TIMEBLOCK
 * @param {Array<string|number>} params.resourceIds 资源 ID 列表
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 *   data 形如 `{ '<blockId>': { commentCount: 2, likeCount: 1 } }`；没有任何互动的资源不会出现在里面。
 *   注意后端实际返回的是**数组**，这里已归一化成对象，见 normalizeSummary
 */
export function getVisibleInteractionSummary(params) {
  const query = new URLSearchParams({
    resourceType: params.resourceType,
    resourceIds: (params.resourceIds || []).map(String).join(',')
  }).toString()
  return request('GET', `/api/v1/interactions/summary-visible?${query}`).then(normalizeSummary)
}

/**
 * 取消点赞
 * @param {string} resourceType WORKSPACE / PROJECT / TIMEBLOCK
 * @param {string|number} resourceId 资源 ID
 * @returns {Promise<{code:number,message:string,data:null,success:boolean,timestamp:string}>}
 */
export function unlike(resourceType, resourceId) {
  const query = new URLSearchParams({
    resourceType,
    resourceId: String(resourceId)
  }).toString()
  return request('DELETE', `/api/v1/likes?${query}`)
}
